import { ReactNode, useRef } from "react";
import Animated, {
  Easing,
  EasingFunction,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { scheduleOnRN } from "react-native-worklets";
import { GetProps, YStack } from "tamagui";

import { useAnimationStore } from "@/store/animation";

// Allows using Tamagui props
const AnimatedYStack = Animated.createAnimatedComponent(YStack);

type AutoExpandingViewProps = {
  children: ReactNode;
  shouldAnimate: boolean;
  animationId?: string;
  duration?: number;
  easing?: EasingFunction;
} & GetProps<typeof YStack>;

/**
 * AutoExpandingView plays an expanding animation from height 0 to the natural height of its children.
 * We measure the inner content first by rendering it absolutely.
 * After we have the measurement, we animate the outer container from height 0 to the measured height.
 */
export default function AutoExpandingView({
  children,
  shouldAnimate,
  animationId,
  duration = 500,
  easing = Easing.inOut(Easing.quad),
  ...props
}: AutoExpandingViewProps) {
  const setHasAnimated = useAnimationStore((s) => s.setHasAnimated);
  const hasAnimated = useAnimationStore((s) =>
    animationId ? s.hasAnimated(animationId) : false,
  );

  const isAnimating = shouldAnimate && !hasAnimated;

  const height = useSharedValue(0);
  const hasStarted = useRef(false);

  const animatedStyle = useAnimatedStyle(
    () => ({
      height: height.value,
    }),
    [height],
  );

  return (
    <AnimatedYStack
      overflow="hidden"
      style={isAnimating ? animatedStyle : undefined}
      {...props}
    >
      <YStack
        position={isAnimating ? "absolute" : undefined}
        width="100%"
        onLayout={(e) => {
          const measuredHeight = e.nativeEvent.layout.height;
          if (
            !shouldAnimate ||
            hasStarted.current ||
            hasAnimated ||
            measuredHeight === 0
          ) {
            return;
          }
          hasStarted.current = true;
          height.value = withTiming(
            measuredHeight,
            { duration, easing },
            (finished) => {
              if (finished && animationId) {
                scheduleOnRN(setHasAnimated, animationId);
              }
            },
          );
        }}
      >
        {children}
      </YStack>
    </AnimatedYStack>
  );
}
